/**
 * AppHeader — global header per design README §3: product name top-left
 * (click = portfolio home = Escape Hatch), freshness + manual refresh for the
 * current data view, signed-in identity top-right with a sign-out action.
 *
 * ADR-003 D8: the signed-in slot also surfaces the auth kind. Customer users
 * (gateway /api/auth) see just their username; staff users (BFF /api/staff-auth)
 * see "Employee · Support" so they can confirm which surface they're on before
 * clicking sign out.
 *
 * Sign-out: a single click calls AuthStore.logout(), which dispatches to the
 * matching endpoint by authKind (employee → /api/staff-auth/logout;
 * customer → /api/auth/logout) then clears local state in either case.
 * AuthGate re-renders the login page on the next tick.
 */

import type { ReactNode } from 'react'
import type { PeriodSelection } from '@domain/types'
import { useAuthStore } from '@stores/authStore'
import { Link } from './Link'

export function AppHeader({ period, children }: { period: PeriodSelection; children?: ReactNode }) {
  const currentUser = useAuthStore((s) => s.currentUser)
  const authKind = useAuthStore((s) => s.authKind)
  const logout = useAuthStore((s) => s.logout)

  function handleSignOut(): void {
    void logout()
  }

  const isEmployee = authKind === 'employee'

  return (
    <header className="app-header" data-testid="app-header">
      <Link to="/" className="app-title" testid="app-title" period={period}>
        Smart Invoice Health
      </Link>
      <div className="app-header-controls">
        {children}
        {currentUser !== null ? (
          <div className="app-header-user" data-testid="app-header-user">
            <span
              className="app-header-user-name"
              data-testid="app-header-user-name"
              title={currentUser.displayName}
            >
              {currentUser.displayName}
            </span>
            <span
              className={
                isEmployee
                  ? 'app-header-user-kind app-header-user-kind-employee'
                  : 'app-header-user-kind'
              }
              data-testid="app-header-user-kind"
            >
              {isEmployee ? `Employee · ${currentUser.role}` : 'Customer'}
            </span>
            <button
              type="button"
              className="app-header-signout"
              onClick={handleSignOut}
              data-testid="app-header-signout"
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  )
}
